import { Icon } from "@iconify/react";
import { useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import {
  PlanTypeV4,
  MembershipState,
  MembershipPromoCode,
} from "@markit/common.types";
import React, { useCallback, useMemo, useState } from "react";
import { MembershipPromoCodeModal } from "../../../components/CreatorDashboard/Memberships/MembershipPromoCodeModal";
import {
  MEMBERSHIP_PROMO_FLEX_SECTIONS,
  MembershipPromoCodeItem,
} from "../../../components/CreatorDashboard/Memberships/MembershipPromoCodeItem";
import { hasSubscription } from "@markit/common.utils";
import SearchBoxContainer from "../../../components/Containers/SearchBoxContainer";
import filter from "lodash.filter";
import BlackHoverButton from "../../../components/Buttons/BlackHoverButton";
import EmptyStateButton from "../../../components/Buttons/EmptyStateButton";
import AlertContainer from "../../../components/Containers/AlertContainer";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { TabPanel } from "../../../components/FullEventSubComponents/TabPanel";
import { notificationActions } from "../../../redux/slices/notificationSlice";
import { useLoadMembershipPromoCodes } from "../../../hooks/useLoadMembershipPromoCodes";
import { useOnMount } from "../../../hooks/useOnMount";
import StandardListContainer from "../../../components/Containers/StandardListContainer";
import ListTabs from "../../../components/DropdownsAndTabs/ListTabs";
import FlatlistLoadMore from "../../../components/FlatlistLoadMore";
import { EmptyStateFlatlist } from "../../../components/EmptyStates/EmptyStateFlatlist";
import EmptyStateButtonSubscription from "../../Buttons/EmptyState/EmptyStateButtonSubscription";

type MembershipsPromoCodePanelProps = {
  tabValue: number;
  setTabValue: (tabValue: number) => void;
};

export const MembershipsPromoCodePanel = (
  props: MembershipsPromoCodePanelProps
) => {
  const { tabValue, setTabValue } = props;
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const [isPromoModalVisible, setIsPromoModalVisible] =
    useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [listValue, setListValue] = useState(0);
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });

  const {
    isFinished: activeIsFinished,
    isLoading: activeIsLoading,
    fetchedPromoCodes: activeFetchedPromoCodes,
    fetchPromoCodes: activeFetchPromoCodes,
    fetchMorePromoCodes: activeFetchMorePromoCodes,
    loadSearchResultsThrottled: activeLoadSearchResultsThrottled,
  } = useLoadMembershipPromoCodes({
    windowSize: 10,
    active: true,
  });

  const {
    isFinished: inactiveIsFinished,
    isLoading: inactiveIsLoading,
    fetchedPromoCodes: inactiveFetchedPromoCodes,
    fetchPromoCodes: inactiveFetchPromoCodes,
    fetchMorePromoCodes: inactiveFetchMorePromoCodes,
    loadSearchResultsThrottled: inactiveLoadSearchResultsThrottled,
  } = useLoadMembershipPromoCodes({
    windowSize: 10,
    active: false,
  });

  useOnMount(() => {
    Promise.all([activeFetchPromoCodes(), inactiveFetchPromoCodes()]);
  });

  const tabLabels = useMemo(() => ["Active", "Inactive"], []);

  const paidMembershipsEnabled = useMemo(() => {
    return accountData.membership.state === MembershipState.ACTIVE;
  }, [accountData.membership.state]);

  const hasSub = useMemo(() => hasSubscription(accountData), [accountData]);

  const handleSearch = (text: string) => {
    const lowerCaseText = text.toLowerCase();
    if (text !== "") {
      if (listValue === 0) {
        activeLoadSearchResultsThrottled(text);
      } else {
        inactiveLoadSearchResultsThrottled(text);
      }
    }
    setSearchTerm(lowerCaseText);
  };

  const contains = useCallback((item: MembershipPromoCode, query: string) => {
    return item.alias.toLowerCase().includes(query);
  }, []);

  const fetchedPromoCodes = useMemo(
    () =>
      listValue === 0 ? activeFetchedPromoCodes : inactiveFetchedPromoCodes,
    [listValue, activeFetchedPromoCodes, inactiveFetchedPromoCodes]
  );

  const isLoading = useMemo(
    () => (listValue === 0 ? activeIsLoading : inactiveIsLoading),
    [listValue, activeIsLoading, inactiveIsLoading]
  );

  const isFinished = useMemo(
    () => (listValue === 0 ? activeIsFinished : inactiveIsFinished),
    [listValue, activeIsFinished, inactiveIsFinished]
  );

  const loadMorePromoCodes = useMemo(
    () =>
      listValue === 0 ? activeFetchMorePromoCodes : inactiveFetchMorePromoCodes,
    [listValue, activeFetchMorePromoCodes, inactiveFetchMorePromoCodes]
  );

  const promoCodesToShow = useMemo(() => {
    if (searchTerm === "") {
      return fetchedPromoCodes;
    } else {
      return filter(fetchedPromoCodes, (code) => {
        return contains(code, searchTerm);
      });
    }
  }, [contains, fetchedPromoCodes, searchTerm]);

  const emptyStateOnPress = useCallback(() => {
    if (paidMembershipsEnabled && hasSub) {
      setIsPromoModalVisible(true);
    } else if (hasSub) {
      setTabValue(2);
    } else {
      dispatch(
        notificationActions.setMarkitPlusModal(PlanTypeV4.ESSENTIAL.name)
      );
    }
  }, [paidMembershipsEnabled, hasSub, setTabValue, dispatch]);

  const renderEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoading}
        containerStyles={{ paddingTop: 100 }}
        nonSearchEmptyView={
          !hasSub ? (
            <EmptyStateButtonSubscription
              planType={PlanTypeV4.ESSENTIAL}
              iconName={"ion:ribbon"}
              description="Start earning by creating paid subscriber plans"
              btnText="Get Essential Plan"
            />
          ) : paidMembershipsEnabled ? (
            <EmptyStateButton
              title="No Promo Codes"
              description="Create a new promo code"
              icon={
                <Icon
                  icon={"ion:pricetags"}
                  height={49}
                  style={{ color: Colors.GRAY1 }}
                />
              }
              iconBox={73}
              iconBackgroundColor={Colors.GRAY6}
              btnText="New Promo"
              onPress={emptyStateOnPress}
              containerStyles={{ paddingTop: 120 }}
            />
          ) : (
            <EmptyStateButton
              title="Enable Paid Plans to Start Earning"
              description="Your promo codes will show up here once you connect to Stripe and enable paid plans."
              icon={
                <Icon
                  icon={"ion:ribbon"}
                  height={50}
                  style={{ color: Colors.GREEN2 }}
                />
              }
              iconBox={73}
              iconBackgroundColor={Colors.WHITE1}
              btnText="Enable Paid Plans"
              onPress={emptyStateOnPress}
              containerStyles={{ paddingTop: 120 }}
              buttonContainerStyles={{ backgroundColor: Colors.GREEN2 }}
            />
          )
        }
      />
    );
  }, [
    emptyStateOnPress,
    hasSub,
    isLoading,
    paidMembershipsEnabled,
    searchTerm,
  ]);

  const renderItem = useCallback((item: MembershipPromoCode) => {
    return <MembershipPromoCodeItem key={item.id} item={item} />;
  }, []);

  return (
    <TabPanel value={tabValue} index={1}>
      <StandardListContainer
        searchComp={
          <div className="ColumnNormal" style={{ gap: 14, width: "100%" }}>
            <div className="AlignedRowSpaced" style={{ gap: 14 }}>
              <SearchBoxContainer
                placeholder="Search Promo Codes..."
                onChange={(e: any) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0 }}
              />
              <BlackHoverButton
                title={"New Promo"}
                iconName="ion:add-circle"
                iconSize={19}
                onPress={() => setIsPromoModalVisible(true)}
                permanentHover
                containerStyles={{
                  paddingInline: 14,
                  paddingBlock: 12,
                  whiteSpace: "nowrap",
                }}
                disabled={!paidMembershipsEnabled}
              />
            </div>
            <ListTabs
              tabLabels={tabLabels}
              tabNumbers={[-1, -1]}
              selectedValue={listValue}
              onChange={setListValue}
              altColor={Colors.BLACK}
            />
          </div>
        }
        gridTable={{
          flexSections: MEMBERSHIP_PROMO_FLEX_SECTIONS,
          sectionTitles: [
            "Promo Code",
            "Percent Off",
            "Discount Duration",
            "Uses",
          ],
        }}
        listComp={
          <FlatlistLoadMore
            fullList={fetchedPromoCodes}
            currentList={promoCodesToShow}
            renderItem={(item: MembershipPromoCode) => renderItem(item)}
            renderWhenEmpty={renderEmptyView}
            isLoadingMore={isLoading}
            showLoadMore={searchTerm.trim() === "" && !isFinished}
            loadMoreOnPress={loadMorePromoCodes}
          />
        }
      />
      {isPromoModalVisible ? (
        <MembershipPromoCodeModal
          accountData={accountData}
          setIsPromoModalVisible={setIsPromoModalVisible}
        />
      ) : null}

      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        theme={theme}
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </TabPanel>
  );
};
